<template>
  <NavBar pageName="Host Access Control"></NavBar>
  <v-container style="margin-top: 185px;">
    <v-btn variant="text" size="small" @click="$router.push('/settings')" class="mb-2">
      <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
      Settings
    </v-btn>

    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab value="hosts">Hosturi Permise</v-tab>
      <v-tab value="logs">Log Blocari</v-tab>
    </v-tabs>

    <!-- TAB 1: Allowed Hosts -->
    <v-window v-model="activeTab">
      <v-window-item value="hosts">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Hosturi Permise</span>
            <v-btn color="success" size="small" @click="openAddDialog">
              <v-icon class="mr-1">mdi-plus</v-icon>
              Adauga
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-text-field
              v-model="hostsSearch"
              prepend-inner-icon="mdi-magnify"
              label="Cauta dupa host"
              variant="outlined"
              density="compact"
              clearable
              class="mb-3"
              style="max-width: 400px;"
            ></v-text-field>

            <v-data-table
              :headers="hostsHeaders"
              :items="filteredHosts"
              :loading="hostsLoading"
              :items-per-page="20"
              class="elevation-1"
            >
              <template v-slot:[`item.host_type`]="{ item }">
                <v-chip size="small" :color="hostTypeColor(item.host_type)">
                  {{ item.host_type }}
                </v-chip>
              </template>

              <template v-slot:[`item.active`]="{ item }">
                <v-chip size="small" :color="item.active ? 'success' : 'grey'">
                  {{ item.active ? 'Activ' : 'Inactiv' }}
                </v-chip>
              </template>

              <template v-slot:[`item.created_at`]="{ item }">
                {{ formatDateTime(item.created_at) }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-btn icon size="small" variant="text" color="primary" @click="openEditDialog(item)" title="Editeaza">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon size="small" variant="text" color="error" @click="openDeleteDialog(item)" title="Sterge">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-window-item>

      <!-- TAB 2: Blocked Logs -->
      <v-window-item value="logs">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Log Blocari</span>
            <v-btn color="error" size="small" @click="openClearLogsDialog" title="Sterge loguri vechi">
              <v-icon class="mr-1">mdi-delete-sweep</v-icon>
              Curata loguri
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row class="mb-3" dense>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="logsFilters.host_value"
                  label="Host"
                  variant="outlined"
                  density="compact"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="logsFilters.remote_ip"
                  label="IP"
                  variant="outlined"
                  density="compact"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="logsFilters.date_from"
                  label="De la"
                  variant="outlined"
                  density="compact"
                  type="date"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="2">
                <v-text-field
                  v-model="logsFilters.date_to"
                  label="Pana la"
                  variant="outlined"
                  density="compact"
                  type="date"
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="1" class="d-flex align-center">
                <v-btn icon size="small" color="primary" @click="loadLogs" title="Filtreaza" :loading="logsLoading">
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-data-table
              :headers="logsHeaders"
              :items="logs"
              :loading="logsLoading"
              :items-per-page="50"
              class="elevation-1"
            >
              <template v-slot:[`item.blocked_at`]="{ item }">
                {{ formatDateTime(item.blocked_at) }}
              </template>

              <template v-slot:[`item.request_method`]="{ item }">
                <v-chip size="x-small" :color="methodColor(item.request_method)">
                  {{ item.request_method }}
                </v-chip>
              </template>
            </v-data-table>

            <div v-if="logsPagination.total_pages > 1" class="d-flex justify-center mt-3">
              <v-pagination
                v-model="logsPage"
                :length="logsPagination.total_pages"
                :total-visible="5"
                density="compact"
                @update:modelValue="loadLogs"
              ></v-pagination>
            </div>
          </v-card-text>
        </v-card>
      </v-window-item>
    </v-window>

    <!-- Add/Edit Host Dialog -->
    <v-dialog v-model="showDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title>
          {{ isEditing ? 'Editeaza Host' : 'Adauga Host' }}
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="formData.host_value"
            label="Host / IP / CIDR"
            variant="outlined"
            density="compact"
            :rules="[v => !!v || 'Valoarea este obligatorie']"
            class="mb-3"
            placeholder="ex: my.veridian.ro, 192.168.1.0/24"
          ></v-text-field>

          <v-select
            v-model="formData.host_type"
            :items="hostTypes"
            label="Tip"
            variant="outlined"
            density="compact"
            class="mb-3"
          ></v-select>

          <v-text-field
            v-model="formData.description"
            label="Descriere"
            variant="outlined"
            density="compact"
            class="mb-3"
            placeholder="ex: Server productie"
          ></v-text-field>

          <v-switch
            v-model="formData.active"
            label="Activ"
            color="success"
            density="compact"
          ></v-switch>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeDialog">Anuleaza</v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="saveHost"
            :loading="saving"
            :disabled="!formData.host_value"
          >
            {{ isEditing ? 'Actualizeaza' : 'Salveaza' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Sterge Host</v-card-title>
        <v-card-text>
          Esti sigur ca vrei sa stergi <strong>{{ deletingItem?.host_value }}</strong>?
          <br><br>
          Aceasta actiune nu poate fi anulata.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showDeleteDialog = false">Anuleaza</v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete" :loading="deleting">
            Sterge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Clear Logs Dialog -->
    <v-dialog v-model="showClearLogsDialog" max-width="400px">
      <v-card>
        <v-card-title>Curata Loguri</v-card-title>
        <v-card-text>
          Sterge logurile mai vechi de:
          <v-text-field
            v-model.number="clearLogsDays"
            label="Zile"
            variant="outlined"
            density="compact"
            type="number"
            min="1"
            class="mt-3"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="showClearLogsDialog = false">Anuleaza</v-btn>
          <v-btn color="error" variant="flat" @click="confirmClearLogs" :loading="clearingLogs">
            Sterge
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import NavBar from "@/components/NavBar.vue";
import store from "@/store";
import { log } from "@/composables/useLog";
import { getAllowedHosts, saveAllowedHost, deleteAllowedHost, getBlockedHostLogs, clearBlockedHostLogs } from "@/composables/useAllowedHostsAPI";

export default defineComponent({
  name: 'HostAccessControlView',
  components: {
    NavBar
  },
  data() {
    return {
      activeTab: 'hosts',
      // Hosts tab
      hostsLoading: false,
      hosts: [],
      hostsSearch: '',
      // Logs tab
      logsLoading: false,
      logs: [],
      logsPage: 1,
      logsPagination: { total: 0, total_pages: 1 },
      logsFilters: {
        host_value: '',
        remote_ip: '',
        date_from: '',
        date_to: ''
      },
      // Dialogs
      showDialog: false,
      showDeleteDialog: false,
      showClearLogsDialog: false,
      isEditing: false,
      editingId: null,
      deletingItem: null,
      saving: false,
      deleting: false,
      clearingLogs: false,
      clearLogsDays: 30,
      formData: {
        host_value: '',
        host_type: 'hostname',
        description: '',
        active: true
      },
      hostTypes: ['hostname', 'ip', 'cidr'],
      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  computed: {
    token() {
      return store.getters.getToken;
    },
    hostsHeaders() {
      return [
        { title: 'ID', key: 'id', width: '60px' },
        { title: 'Host / IP', key: 'host_value' },
        { title: 'Tip', key: 'host_type', width: '100px' },
        { title: 'Descriere', key: 'description' },
        { title: 'Status', key: 'active', width: '100px' },
        { title: 'Creat', key: 'created_at', width: '150px' },
        { title: 'Actiuni', key: 'actions', sortable: false, width: '120px' }
      ];
    },
    logsHeaders() {
      return [
        { title: 'Data', key: 'blocked_at', width: '160px' },
        { title: 'Host', key: 'host_value' },
        { title: 'IP', key: 'remote_ip', width: '140px' },
        { title: 'Metoda', key: 'request_method', width: '80px' },
        { title: 'Path', key: 'request_path' },
        { title: 'User Agent', key: 'user_agent' }
      ];
    },
    filteredHosts() {
      if (!this.hostsSearch) return this.hosts;
      const s = this.hostsSearch.toLowerCase();
      return this.hosts.filter(item =>
        item.host_value?.toLowerCase().includes(s) ||
        item.description?.toLowerCase().includes(s)
      );
    }
  },
  methods: {
    // --- Hosts ---
    async loadHosts() {
      this.hostsLoading = true;
      try {
        const response = await getAllowedHosts(this.token);
        if (!response.error) {
          this.hosts = response.hosts || [];
        } else {
          this.showMessage(response.message || 'Eroare la incarcarea hosturilor', 'error');
        }
      } catch (err) {
        log("Error loading hosts:", err);
        this.showMessage('Eroare la incarcarea hosturilor', 'error');
      } finally {
        this.hostsLoading = false;
      }
    },

    openAddDialog() {
      this.isEditing = false;
      this.editingId = null;
      this.formData = { host_value: '', host_type: 'hostname', description: '', active: true };
      this.showDialog = true;
    },

    openEditDialog(item) {
      this.isEditing = true;
      this.editingId = item.id;
      this.formData = {
        host_value: item.host_value,
        host_type: item.host_type || 'hostname',
        description: item.description || '',
        active: item.active
      };
      this.showDialog = true;
    },

    closeDialog() {
      this.showDialog = false;
    },

    async saveHost() {
      this.saving = true;
      try {
        const data = { ...this.formData };
        if (this.isEditing) {
          data.id = this.editingId;
        }
        const response = await saveAllowedHost(this.token, data);
        if (!response.error) {
          this.showMessage(response.message || 'Host salvat', 'success');
          this.closeDialog();
          await this.loadHosts();
        } else {
          this.showMessage(response.message || 'Eroare la salvarea hostului', 'error');
        }
      } catch (err) {
        log("Error saving host:", err);
        this.showMessage('Eroare la salvarea hostului', 'error');
      } finally {
        this.saving = false;
      }
    },

    openDeleteDialog(item) {
      this.deletingItem = item;
      this.showDeleteDialog = true;
    },

    async confirmDelete() {
      this.deleting = true;
      try {
        const response = await deleteAllowedHost(this.token, this.deletingItem.id);
        if (!response.error) {
          this.showMessage(response.message || 'Host sters', 'success');
          this.showDeleteDialog = false;
          await this.loadHosts();
        } else {
          this.showMessage(response.message || 'Eroare la stergerea hostului', 'error');
        }
      } catch (err) {
        log("Error deleting host:", err);
        this.showMessage('Eroare la stergerea hostului', 'error');
      } finally {
        this.deleting = false;
      }
    },

    // --- Logs ---
    async loadLogs() {
      this.logsLoading = true;
      try {
        const filters = { page: this.logsPage, per_page: 50 };
        if (this.logsFilters.host_value) filters.host_value = this.logsFilters.host_value;
        if (this.logsFilters.remote_ip) filters.remote_ip = this.logsFilters.remote_ip;
        if (this.logsFilters.date_from) filters.date_from = this.logsFilters.date_from;
        if (this.logsFilters.date_to) filters.date_to = this.logsFilters.date_to;

        const response = await getBlockedHostLogs(this.token, filters);
        if (!response.error) {
          this.logs = response.logs || [];
          this.logsPagination = response.pagination || { total: 0, total_pages: 1 };
        } else {
          this.showMessage(response.message || 'Eroare la incarcarea logurilor', 'error');
        }
      } catch (err) {
        log("Error loading logs:", err);
        this.showMessage('Eroare la incarcarea logurilor', 'error');
      } finally {
        this.logsLoading = false;
      }
    },

    openClearLogsDialog() {
      this.clearLogsDays = 30;
      this.showClearLogsDialog = true;
    },

    async confirmClearLogs() {
      this.clearingLogs = true;
      try {
        const response = await clearBlockedHostLogs(this.token, this.clearLogsDays);
        if (!response.error) {
          this.showMessage(response.message || 'Loguri curatate', 'success');
          this.showClearLogsDialog = false;
          await this.loadLogs();
        } else {
          this.showMessage(response.message || 'Eroare la curatarea logurilor', 'error');
        }
      } catch (err) {
        log("Error clearing logs:", err);
        this.showMessage('Eroare la curatarea logurilor', 'error');
      } finally {
        this.clearingLogs = false;
      }
    },

    // --- Helpers ---
    hostTypeColor(type) {
      const colors = { hostname: 'blue', ip: 'green', cidr: 'orange' };
      return colors[type] || 'grey';
    },

    methodColor(method) {
      const colors = { GET: 'blue', POST: 'green', PUT: 'orange', DELETE: 'red' };
      return colors[method] || 'grey';
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },

    showMessage(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  },
  mounted() {
    this.loadHosts();
    this.loadLogs();
  }
});
</script>
